import { useCallback, useState } from "react";
import { useApp } from "../../../../context/AppContext";
import { useTransaction } from "../../../../context/TransactionContext";
import { notifyError } from "../../../../utils/notify";
import { CalendarEventType } from "../../../../types/calendar";
import {
  EditTransactionLineItemsResponse,
  EditTransactionRequest,
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../../types/transactions";
import {
  transformTransactionResponseToGetTransactionsResponse,
  transformTransactionToEvent,
} from "../../../../utils/transactionUtils";
import { findTransactionById } from "../../../../utils/transactionLookup";
import { replaceTransactionEvents } from "../calendarEventUtils";
import { useTypedCalendarEvent } from "../calendarViewTypes";

interface UseTransactionStoreArgs {
  timezone: string | undefined;
  salonServices: any[];
}

/**
 * The calendar's transaction store. TransactionContext's list is the single
 * source every write path upserts into (local edits, status changes, drag
 * reschedules, Pusher/sync refetches), and `findTransaction` is the one lookup
 * the popover / modal / drag paths use. Also owns the Transaction Detail
 * modal's selected transaction + open state.
 */
export function useTransactionStore({
  timezone,
  salonServices,
}: UseTransactionStoreArgs) {
  const { editTransaction } = useApp();
  const { transactions, fetchTransaction, upsertTransaction } =
    useTransaction();
  const { setEvents } = useTypedCalendarEvent();

  const [selectedTransaction, setSelectedTransaction] =
    useState<GetTransactionsResponse | null>(null);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isLoadingNewTransaction, setIsLoadingNewTransaction] = useState(false);

  const findTransaction = useCallback(
    (transactionId: string) =>
      findTransactionById(transactionId, transactions, selectedTransaction),
    [transactions, selectedTransaction],
  );

  const openTransactionModal = useCallback(
    (transaction: GetTransactionsResponse) => {
      setSelectedTransaction(transaction);
      setIsTransactionModalOpen(true);
    },
    [],
  );

  const closeTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(false);
    setSelectedTransaction(null);
  }, []);

  // Upsert into the store and regenerate the transaction's calendar events.
  const storeTransaction = useCallback(
    (transactionData: GetTransactionsResponse) => {
      upsertTransaction(transactionData);
      const nextEvents = transformTransactionToEvent(
        transactionData,
        timezone || "",
        salonServices,
      ) as CalendarEventType[];
      setEvents((prev) =>
        replaceTransactionEvents(
          prev,
          transactionData.transaction.id,
          nextEvents,
        ),
      );
    },
    [upsertTransaction, timezone, salonServices, setEvents],
  );

  // Pusher-driven refetch (appointment notifications, transaction.sync) and
  // deep links: fetch one transaction and store it.
  const refetchAndUpdateTransaction = useCallback(
    async (transactionId: string, errorContext: string): Promise<void> => {
      try {
        const transactionResponse = await fetchTransaction(transactionId);
        if (!transactionResponse) return;
        storeTransaction(
          transformTransactionResponseToGetTransactionsResponse(
            transactionResponse,
          ),
        );
      } catch (error) {
        console.error(
          `Error fetching transaction from ${errorContext}:`,
          error,
        );
      }
    },
    [fetchTransaction, storeTransaction],
  );

  // Local edit from the Transaction Detail modal / popover sub-surfaces.
  const handleTransactionUpdated = useCallback(
    async (
      updatedTransaction: GetTransactionsResponse | TransactionResponse,
    ): Promise<void> => {
      // Ensure we have GetTransactionsResponse format
      const transactionData = updatedTransaction as GetTransactionsResponse;
      setSelectedTransaction(transactionData);
      storeTransaction(transactionData);
    },
    [storeTransaction],
  );

  /**
   * After a successful drag-edit, propagate the response to the store (which
   * the popover / modal lookups read), the open modal's selectedTransaction,
   * and the calendar events (regenerated so the block reflects the backend's
   * duration / calc__end_dt instead of the optimistic snap).
   */
  const propagateBookedServiceEdit = useCallback(
    (
      transactionId: string,
      responseLineItems: EditTransactionLineItemsResponse["line_items"],
    ) => {
      const existing = findTransaction(transactionId);
      if (!existing) return;

      const merged: GetTransactionsResponse = {
        ...existing,
        line_items: responseLineItems,
      };
      setSelectedTransaction((prev) =>
        prev?.transaction.id === transactionId ? merged : prev,
      );
      storeTransaction(merged);
    },
    [findTransaction, storeTransaction],
  );

  // Persist an appointment's status change and refresh the calendar (store,
  // events). Shared by the preview popover and the card view's drag-and-drop,
  // so both take the same write path and surface the same failure toast.
  // Resolves with the updated transaction, or null if the write didn't take
  // (the caller then leaves its own UI on the old status).
  const applyAppointmentStatus = useCallback(
    async (
      transaction: GetTransactionsResponse,
      newStatus: string,
    ): Promise<GetTransactionsResponse | null> => {
      // Friendly verb for the failure toast ("accept"/"confirm"/"update").
      const actionVerb =
        newStatus === "Accepted"
          ? "accept"
          : newStatus === "Confirmed"
            ? "confirm"
            : "update";
      const notifyFailure = (error: unknown) => {
        console.error("Failed to update appointment status:", error);
        notifyError(
          `We couldn't ${actionVerb} this appointment. Please try again.`,
          { title: "Update failed" },
        );
      };

      const statusUpdateData: EditTransactionRequest = {
        transaction_id: transaction.transaction.id,
        appointment_status:
          newStatus as EditTransactionRequest["appointment_status"],
      };

      try {
        const response = await editTransaction(statusUpdateData);
        // A falsy response means the write didn't take — surface it rather
        // than silently leaving the UI on the old status.
        if (!response) {
          notifyFailure(new Error("editTransaction returned no response"));
          return null;
        }

        const updatedTransaction: GetTransactionsResponse = {
          ...transaction,
          transaction: {
            ...transaction.transaction,
            appointment_status: newStatus,
          },
        };

        await handleTransactionUpdated(updatedTransaction);
        return updatedTransaction;
      } catch (error) {
        notifyFailure(error);
        return null;
      }
    },
    [editTransaction, handleTransactionUpdated],
  );

  // Card view: a card dropped on another column changes that appointment's
  // status. Dropping on Completed is handled in CardView by opening the
  // transaction modal instead, since completing an appointment means taking
  // payment.
  const handleCardStatusUpdate = useCallback(
    async (transaction: GetTransactionsResponse, newStatus: string) => {
      await applyAppointmentStatus(transaction, newStatus);
    },
    [applyAppointmentStatus],
  );

  // Fetch a just-created (rebooked) appointment and open it in the modal.
  const handleViewNewAppointment = useCallback(
    async (transactionId: string) => {
      try {
        setIsLoadingNewTransaction(true);
        const transactionResponse = await fetchTransaction(transactionId);
        if (transactionResponse) {
          openTransactionModal(
            transformTransactionResponseToGetTransactionsResponse(
              transactionResponse,
            ),
          );
        }
      } catch (error) {
        console.error("Error fetching new transaction:", error);
      } finally {
        setIsLoadingNewTransaction(false);
      }
    },
    [fetchTransaction, openTransactionModal],
  );

  return {
    transactions,
    selectedTransaction,
    isTransactionModalOpen,
    isLoadingNewTransaction,
    findTransaction,
    openTransactionModal,
    closeTransactionModal,
    storeTransaction,
    refetchAndUpdateTransaction,
    handleTransactionUpdated,
    propagateBookedServiceEdit,
    applyAppointmentStatus,
    handleCardStatusUpdate,
    handleViewNewAppointment,
  };
}
